import { apiClient } from "@base/api/client";
import { useCurrentUserQuery } from "@base/model/hooks";
import type { PaginationResponse } from "@base/model/types";
import { type InfiniteData, useMutation, useQueryClient } from "@tanstack/react-query";
import type { Comment } from "./schema";

type CommentsPageResponse = PaginationResponse<Comment[]>;
type CommentsInfiniteData = InfiniteData<CommentsPageResponse>;

async function createComment(projectId: string, logId: string, content: string): Promise<Comment> {
  const response = await apiClient.post<Comment>(
    `/project-management/api/projects/${projectId}/daily-logs/${logId}/comments`,
    { content },
  );
  return response.data;
}

function addCommentToFirstPage(
  old: CommentsInfiniteData | undefined,
  comment: Comment,
): CommentsInfiniteData | undefined {
  if (!old) return old;
  const [firstPage, ...restPages] = old.pages;
  if (!firstPage) return old;

  return {
    ...old,
    pages: [{ ...firstPage, results: [comment, ...firstPage.results], count: firstPage.count + 1 }, ...restPages],
  };
}

function replaceCommentInFirstPage(
  old: CommentsInfiniteData | undefined,
  optimisticId: number,
  realComment: Comment,
): CommentsInfiniteData | undefined {
  if (!old) return old;
  const [firstPage, ...restPages] = old.pages;
  if (!firstPage) return old;

  return {
    ...old,
    pages: [
      { ...firstPage, results: firstPage.results.map((c) => (c.id === optimisticId ? realComment : c)) },
      ...restPages,
    ],
  };
}

function removeCommentFromFirstPage(
  old: CommentsInfiniteData | undefined,
  optimisticId: number,
): CommentsInfiniteData | undefined {
  if (!old) return old;
  const [firstPage, ...restPages] = old.pages;
  if (!firstPage) return old;

  return {
    ...old,
    pages: [
      {
        ...firstPage,
        results: firstPage.results.filter((c) => c.id !== optimisticId),
        count: Math.max(0, firstPage.count - 1),
      },
      ...restPages,
    ],
  };
}

export function useCreateCommentMutation(projectId: string, logId: string) {
  const queryClient = useQueryClient();
  const { data: currentUserData } = useCurrentUserQuery();
  const commentsQueryKey = ["comments", projectId, logId] as const;

  return useMutation({
    mutationFn: (content: string) => createComment(projectId, logId, content),

    onMutate: async (content) => {
      await queryClient.cancelQueries({ queryKey: commentsQueryKey });

      const optimisticId = -Date.now();
      const optimisticComment: Comment = {
        id: optimisticId,
        user: { id: currentUserData?.user.id ?? -1, name: currentUserData?.user.name ?? "", avatar_url: "" },
        content,
        write_time: new Date().toISOString(),
        is_owner: true,
      };

      queryClient.setQueryData<CommentsInfiniteData>(commentsQueryKey, (old) =>
        addCommentToFirstPage(old, optimisticComment),
      );

      return { optimisticId };
    },

    onSuccess: (newComment, _content, context) => {
      if (!context) return;
      queryClient.setQueryData<CommentsInfiniteData>(commentsQueryKey, (old) =>
        replaceCommentInFirstPage(old, context.optimisticId, newComment),
      );
    },

    onError: (_error, _content, context) => {
      if (!context) return;
      queryClient.setQueryData<CommentsInfiniteData>(commentsQueryKey, (old) =>
        removeCommentFromFirstPage(old, context.optimisticId),
      );
    },
  });
}
